import React from "react";
import {
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "./auth.context";
import { authServiceFactory } from "../services/auth.service";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

interface OAuthProps {
  clientName?: string;
  scopes?: string[];
}

export const OAuthComponent: React.FC<OAuthProps> = ({
  clientName = "Application",
  scopes = ["meetings", "profile"]
}) => {
  const [errMsg, setErrMsg] = React.useState("");
  const [selectedAccount, setSelectedAccount] = React.useState("");
  const authContext = React.useContext(AuthContext);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectURI = searchParams.get("redirect_uri");
  const clientID = searchParams.get("client_id");
  const state = searchParams.get("state");
  const codeChallenge = searchParams.get("code_challenge")
  const codeChallengeMethod = searchParams.get("code_challenge_method");


  if (!authContext.access_token) {
    const next = encodeURIComponent(`/authorize?${searchParams.toString()}`);
    return <Navigate to={`/login?next=${next}`} />;
  }

  const onAllow = async () => {
    if (!selectedAccount) {
      setErrMsg("Please select an account");
      return;
    }
    const authService = authServiceFactory();
    if (!clientID || !redirectURI) {
      setErrMsg("Invalid client or redirect URI");
      return;
    }
    const res = await authService.authorizeOAuth(redirectURI, clientID, selectedAccount, state || "", codeChallenge, codeChallengeMethod);
    if (!res || !res.redirectUri) {
      setErrMsg("Failed to authorize");
      return;
    }
    //have to append accountID as extra info
    const uri = `${res.redirectUri}&accountId=${selectedAccount}`
    window.location.href = uri;
  }

  /**
   * Deny the authorization request, as per the standard add the error parameter and redirect to the redirect URI
   */
  const onDeny = async () => {
    if (!redirectURI) {
      setErrMsg("Invalid redirect URI");
      return;
    }
    const errorRedirectURI = `${redirectURI}?error=access_denied`;
    window.location.href = errorRedirectURI;
  }
  return (
    <div className="accounts wrapper">
      <div className="oauth-page-view container">
        <Paper elevation={6} className="oauth-page-view paper">
          <div className="oauth-page-view header oauth-border-bottom">
            <Typography variant="h1">
              Authorization Request
            </Typography>
          </div>
          <div className="oauth-page-view  content">
            <Box className="oauth-page-view  account-section">
              <Typography variant="body1" className="oauth-page-view account-section title oauth-font-medium">
                Select default account for meetings
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="account-select-label">Select Account</InputLabel>
                <Select
                  labelId="account-select-label"
                  id="account-select"
                  value={selectedAccount}
                  label="Select Account"
                  onChange={(e) => setSelectedAccount(e.target.value)}
                >
                  {authContext.memberships.map((account) => (
                    <MenuItem key={account.accountID} value={account.accountID}>
                      {account.accountname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Typography variant="body1" className="oauth-permissions">
              <strong>{clientName}</strong> would like permission to:
            </Typography>

            <List>
              {scopes.map((scope) => (
                <ListItem key={scope} className="oauth-scope-list-item">
                  <ListItemIcon className="oauth-icon-container">
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={scope} />
                </ListItem>
              ))}
            </List>

            <div className="oauth-actions">
              <Button
                variant="contained"
                color="secondary"
                onClick={onDeny}
              >
                Deny
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onAllow}
                disabled={!selectedAccount}
              >
                Allow
              </Button>
            </div>
          </div>
        </Paper>
      </div>
      <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
    </div>
  );
};
